// NOTICE: The intersection observer and initializtion logic for swiper is in the script.js file

// From https://swiperjs.com/api/
import Swiper from 'swiper';
import {
  Navigation,
  Pagination,
  Autoplay,
  FreeMode,
  Mousewheel,
  Controller
} from 'swiper/modules'

// import Swiper and modules styles
import 'swiper/swiper.css';

//import { gsap } from 'gsap'
//import { ScrollTrigger } from 'gsap/ScrollTrigger'

const AUTOPLAY = 5000
const MOBILE_WIDTH = 560 // For stacking on mobile
const mq = window.matchMedia(`(max-width: ${MOBILE_WIDTH - 1}px)`);

// Callback function for initializing a specific swiper
// This is called by the code in script.js
export function initSwiper(element) {
  return new SwiperCarousel(element);
}

// Default export for convenience
export default initSwiper;

class SwiperCarousel {
  get initialOptions() {
    // https://swiperjs.com/api/#parameters
    return {
      // Install modules
      modules: [Navigation, Pagination, Autoplay, FreeMode, Mousewheel, Controller],
      slidesPerView: 'auto',
      watchOverflow: true, // When enabled Swiper will be disabled and hide navigation buttons in case there are not enough slides for sliding
      // preventInteractionOnTransition: true,
      grabCursor: true,
      passiveListeners: true,
      threshold: 0 // Threshold value in px. If "touch distance" will be lower than this value then swiper will not move
    }
  }

  constructor(element) {
    this.swiperInitialized = false
    this.visibleSlides = 1; // Is updated by the updateSlidesPerGroup() function
    this.options = {
      ...this.initialOptions
    }

    // Get options from data attribute, for example data-options="{slidesPerView: 4}"
    const options = element.getAttribute('data-options')
    if (options) {
      this.options = {
        ...this.options,
        ...JSON.parse(options)
      }
    }

    this.element = element
    this.container = element.querySelector('.swiper-container')
    this.navigation = element.querySelector('.swiper-navigation')
    this.pagination = element.querySelector('.swiper-pagination')
    this.styletype = element.getAttribute('swiper-style')
    //this.animatein = this.element.hasAttribute('animatein')
    this.mousewheel = this.element.hasAttribute('mousewheel')
    this.controls = this.element.getAttribute('controls')
    this.stackOnMobile = this.element.hasAttribute('stackonmobile')
    this.swiperType = this.element.getAttribute('swiper-type')
    this.hideNavigationIfAllVisible = this.navigation && this.navigation.hasAttribute('hide-navigation-if-all-visible')

    // Check how many slides we have
    const slides = element.querySelectorAll('.swiper-slide')
    if (!slides || slides.length < 2) {
      // Update the visibility of the navigation
      this.setNavigationVisibility(false)
      return
    }

    // Conditional settings based on the slider type
    if (this.swiperType === 'testimonials') {
      this.options = {
        ...this.options,
        centerInsufficientSlides: true,
        slidesPerGroup: 1, // This is neccessary for the pagination to work correctly on mobile where the next slide is partially visible
        freeMode: {
          sticky: true
        }
      }
    }

    const freemode = this.element.hasAttribute('freemode')
    const freemodeOptions = this.options && this.options.freeMode ? this.options.freeMode : {}
    if (freemode) {
      this.options = {
        ...this.options,
        freeMode: {
          enabled: true,
          sticky: false,
          momentumRatio: 0.4,
          momentumBounce: false,
          momentumVelocityRatio: 0.8,
          ...freemodeOptions
        },
        speed: 400
      }
    }

    if (this.navigation && slides.length > 1) {
      this.options = {
        ...this.options,
        navigation: {
          nextEl: this.navigation.querySelector('.button-next'),
          prevEl: this.navigation.querySelector('.button-prev')
        }
      }
    }

    if (this.pagination) {
      this.options = {
        ...this.options,
        pagination: {
          type: 'bullets',
          clickable: true,
          el: this.pagination
        }
      }
    }

    if (this.mousewheel) {
      this.options = {
        ...this.options,
        mousewheel: {
          forceToAxis: true,
          releaseOnEdges: false,
        }
      }
    }

    this.autoplay = this.element.hasAttribute('autoplay')
    if (this.autoplay) {
      this.options = {
        ...this.options,
        autoplay: {
          delay: AUTOPLAY,
          disableOnInteraction: false
        },
        loop: true,
        speed: 800,
        slidesPerView: 1, // neccesary for loop: true
      }
    }

    if (this.animatein) {
      this.animateIn()
    }

    this.init()
  }

  init(options) {
    if (this.stackOnMobile) {
      // Add event listeners for resizing
      const initSwiperCallback = this.initSwiper.bind(this)
      window.addEventListener('resize', initSwiperCallback)
      window.addEventListener('orientationchange', initSwiperCallback)
    }

    this.initSwiper(options)
  }

  initSwiper(options) {
    if (this.stackOnMobile) {
      if (mq.matches) {
        if (this.swiperInitialized) {
          // Destruct the slider
          this.swiper.destroy()
          this.swiperInitialized = false
        }
        return
      }
    }

    if (this.swiperInitialized) {
      // Already initialized
      return
    }

    this.swiperInitialized = true
    const self = this
    this.swiper = new Swiper(this.container, {
      ...this.options,
      ...options, // This is used to specify the initialSlide in the live page script.js
      on: {
        /*
        slideChange() {
        },
        */
        init() {
          self.updateSlidesPerGroup(this);
          self.updateNavigationVisibility(this);
        },
        resize: function() {
          self.updateSlidesPerGroup(this);
          self.updateNavigationVisibility(this);
        }
        /*
        transitionStart() {
          self.element.classList.toggle('last-slide', this.isEnd)
        }
        */
      }
    })
  }

  reinit() {
    this.swiper.slideTo(0, 0)
  }

  updateSlidesPerGroup(swiper) {
    const swiperWidth = swiper.width;
    const slideWidth = swiper.slides[0]?.offsetWidth || 0;
    const gap = parseInt(getComputedStyle(swiper.el).getPropertyValue('--slide-gap') || 0);

    // Calculate how many full slides fit in view
    const visibleSlides = Math.floor((swiperWidth + gap ) / (slideWidth + gap));

    // Only update if the number of visible slides changed
    if (self.visibleSlides != visibleSlides) {
      swiper.params.slidesPerGroup = Math.max(1, visibleSlides);
      swiper.update();

      self.visibleSlides = visibleSlides
    }
  }

  updateNavigationVisibility(swiper) {
    if (!this.hideNavigationIfAllVisible || !this.navigation) {
      return;
    }

    // Check if swiper is disabled (all slides fit on screen)
    const showNavigation = !swiper.isLocked;

    this.setNavigationVisibility(showNavigation);
  }

  setNavigationVisibility(visible) {
    if (!this.navigation) {
      return;
    }

    if (visible) {
      // Not all slides fit, show navigation
      this.navigation.classList.remove('hide-navigation');
    } else {
      // All slides fit, hide navigation
      this.navigation.classList.add('hide-navigation');
    }
  }

  /*
  animateIn() {
    gsap.registerPlugin(ScrollTrigger)

    const tween = gsap.fromTo(
      this.container,
      {
        x: '30%',
        opacity: 0
      },
      {
        x: 0,
        opacity: 1,
        ease: 'power4.out',
        duration: 1.5,
        rotation: 0.001, // add slight rotation offset for smoothness
        scrollTrigger: {
          trigger: this.element,
          start: '30px bottom',
          end: 'bottom bottom',
          once: true,
          onEnter: instance => {
            if (instance.getVelocity() == 0 && instance.progress > 0.1) {
              // this means it hasn't been scrolled into view, just loaded in the middle of it
              // In that case, just finish the animation, without any duration and disable scrolltrigger
              instance.animation.duration(0)
              instance.animation.play()
            } else {
              gsap.set(this.scrollbar, {
                opacity: 0
              })
            }
          },
          onLeave: () => {
            gsap.to(this.scrollbar, { opacity: 1, duration: 0.8, delay: 0.5 })
          }
        }
      }
    )
  }
  */
}
